import { useEffect, useRef } from 'react';

import { observer } from 'mobx-react';

import { GamesHero } from '../../components/banners/GamesHeroOldSearch';
import { getSlideOrder } from '../../components/FullPageDialogs/config';
import { CategoryGamesGrid } from '../../components/GamesList/CategoryGamesGridOldSearch';
import { Games } from '../../components/GamesList/GamesOldSearch';
import { SearchGamesGrid } from '../../components/GamesList/SearchGamesGridOldSearch';
import { ExternalPayments } from '../../features/externalPayments/ExternalPayments';
import { useSetDailyBonusPopup } from '../../hooks/useDailyBonusPopup';
import { useForceDialogsBeforeFeatureManager } from '../../hooks/useForceDialogsBeforeFeatureManager';
import { useOpenSupportWithQs } from '../../hooks/useOpenSupportWithQs';
import { useSetPromoPopup } from '../../hooks/usePromoPopup';
import { analytics } from '../../services/analytics';
import { HOME_CATEGORY_ID } from '../../stores/categories/CategoriesStore';
import type { CvvErrorListener } from '../../stores/payredeem/types';
import type { FullPageDialogs, Slide } from '../../stores/ui/types';
import { useStores } from '../../stores/useStores';

import styles from './Home.module.scss';

const getHomePageClassname = (prevSlide: Slide | null, openFullPageDialog: FullPageDialogs | null) => {
  const exitingHome = prevSlide === 'home' && openFullPageDialog != null;
  const enteringHome = openFullPageDialog == null && prevSlide != null;

  if (!exitingHome && !enteringHome) {
    return undefined;
  }

  const prevSlideOrder = getSlideOrder(prevSlide);
  const openDialogOrder = getSlideOrder(openFullPageDialog);

  if (prevSlide === 'home') {
    return openDialogOrder > prevSlideOrder ? styles.exitingToLeft : styles.exitingToRight;
  }

  return openDialogOrder > prevSlideOrder ? styles.enteringFromRight : styles.enteringFromLeft;
};

const Home = observer(() => {
  const { categoriesStore, gamesSearchStore, uiStore, rewardsStore, payRedeemStore, portalStore } = useStores();

  const { isSearchInputOpenV2 } = gamesSearchStore;

  const gamesGridRef = useRef<HTMLDivElement>(null);

  const homePageClassName = getHomePageClassname(uiStore.prevSlide, uiStore.openFullPageDialog);

  useForceDialogsBeforeFeatureManager();
  useSetPromoPopup();
  useSetDailyBonusPopup();
  useOpenSupportWithQs();

  useEffect(() => {
    void analytics.sendEvent('lobby_page', {});
  }, [rewardsStore]);

  useEffect(() => {
    const homeScrollListener = () => {
      if (window.scrollY > 0) {
        document.body.classList.add('scrolled');
      } else {
        document.body.classList.remove('scrolled');
      }
    };

    document.addEventListener('scroll', homeScrollListener);
    return () => {
      document.removeEventListener('scroll', homeScrollListener);
    };
  }, []);

  useEffect(() => {
    const cvvErrorListener: CvvErrorListener = (arg) => {
      uiStore.setCloseDialog();
      portalStore.openPortal(() => (
        <ExternalPayments
          errorCode={arg.errorCode}
          errorDetails={arg.errorDetails}
          paymentMethod={arg.paymentMethod}
          presetIframeParams={arg.pendingTransactionParams}
          storeType="store"
        />
      ));
    };

    payRedeemStore.addCvvErrorListener(cvvErrorListener);

    return () => {
      payRedeemStore.removeCvvErrorListener(cvvErrorListener);
    };
  }, [payRedeemStore, portalStore, uiStore]);

  return (
    <section className={homePageClassName}>
      <GamesHero />
      <div ref={gamesGridRef} className={styles.home}>
        {isSearchInputOpenV2 ? (
          <SearchGamesGrid />
        ) : categoriesStore.currentId === HOME_CATEGORY_ID ? (
          <Games />
        ) : (
          <CategoryGamesGrid />
        )}
      </div>
    </section>
  );
});

export default Home;
